import find from 'lodash/find';
import flatMap from 'lodash/flatMap';
import includes from 'lodash/includes';
import uniq from 'lodash/uniq';

import { IAutoQuote, IAutoThirdPartyDataOrders } from '../../../interfaces';
import getLatestDataOrders from '../getLatestDataOrders';

const skippableErrors = ['no_prior_coverage', 'no_eligible_vehicles_on_quote'];

/**
 * Given a quote object, it'll retrieve all third_party_metadata_orders that are failures.
 * Note: as of no_prior, there are skippableErrors, meaning we can proceed through rater calls with failures.
 * The BE will eventually change these failed calls to a success to proceed to checkout.
 *
 * @param {IAutoQuote} quote A quote object.
 * @return {IAutoThirdPartyDataOrders[]} All third_party_data_orders that are either failures or errors with non-skippable errors.
 */

function getNewOrdersWithError(quote: IAutoQuote) {
  const orders = quote?.application?.metadata?.third_party_data_orders || [];
  const latestOrders = getLatestDataOrders(orders);
  const ordersWithError = latestOrders.filter(
    (order) =>
      includes(['error', 'failure'], order?.status) &&
      !skippableErrors.some((skippableError) =>
        order?.error_codes?.includes(skippableError)
      )
  );
  return ordersWithError;
}

export default function handleUnrecoverableDataOrderErrors(
  quote: IAutoQuote,
  analytics?: any
) {
  const ordersWithError = getNewOrdersWithError(quote);
  const actionableErrorCodes = [
    'unverifiable_vins',
    'unverifiable_drivers',
    'ineligible_vehicles',
    'ineligible_drivers',
    'invalid_drivers_licenses',
  ];

  if (!ordersWithError.length) {
    return quote;
  }
  const errorCodes = uniq(
    flatMap(ordersWithError, (order) => order?.error_codes)
  );
  const knockoutErrorCode = find(
    errorCodes,
    (errorCode) => !includes(actionableErrorCodes, errorCode)
  );

  if (!knockoutErrorCode) {
    return quote;
  }
  switch (knockoutErrorCode) {
    case 'household_knockout':
      analytics?.reportNotFound?.({ error_codes: [knockoutErrorCode] });
      throw Object.assign(new Error(), { code: 'household_knockout' });
    default:
      analytics?.reportNotFound?.({ error_codes: ['unknown_error'] });
      throw Object.assign(new Error(), { code: 'unknown_error' });
  }
}
